import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { projectService } from "../services";
import { IProject } from "projects/models";
import { useBoundStore } from "store";
import { projectKeys } from "./useProjects";

export const useUpdateProject = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [data, setData] = useState<IProject | undefined>(undefined);
  const [warnings, setWarnings] = useState<string[]>([]);

  const { tenant, currentOrganization } = useBoundStore();
  const queryClient = useQueryClient();

  const updateProject = async (project: IProject) => {
    try {
      setLoading(true);
      setSuccess(false);
      setError(undefined);
      setData(undefined);
      setWarnings([]);

      if (!project?.id) throw new Error("Project not found.");
      if (!tenant?.id) throw new Error("Tenant not found.");
      if (!currentOrganization?.id) throw new Error("Organization not found.");

      const result = await projectService.updateProject(
        tenant.id,
        currentOrganization.id,
        project.id,
        project
      );

      setData(result.project);
      setWarnings(result.warnings ?? []);
      setSuccess(true);

      // Invalidate project queries to refresh both detail and list views
      // Use setTimeout to ensure React has processed the success state update first
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["projects"],
        });
      }, 0);

      return result;
    } catch (error) {
      setSuccess(false);
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  return {
    updateProject,
    loading,
    success,
    error,
    data,
    warnings,
  };
};
